import {mergeDeep} from '@jobseeker/common-core/utils';
import {locations} from '@jobseeker/lotus/components/JobSearchBar/data';
import {APP, APP_NAME_IIMJOBS, BOTUSERAGENTS} from '../constants';

import type {MetaElement, MetaType} from '@configs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import 'dayjs/locale/en';

import type {GetServerSidePropsContext} from 'next/types';
import {RegistrationTypes} from '../types';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

type Tabs = {
  dataId: string;
  label: string;
  tag: string;
  menu?: Tabs[];
};

/**
 * This function consolidates the app configuration based on app name.
 * @param meta meta config that needs to filtered out
 * @param path current page name, which to be loaded
 * @param isCommon boolean type signifies whether need the common
 *                 configuration or not i.e header or footer
 * @returns the filtered configuration object
 */
const getConfigByApp = (
  meta: MetaType<MetaElement>,
  path: string,
  isCommon: boolean,
  page?: string
) => {
  const APP_NAME = process.env.NEXT_PUBLIC_APP_NAME || APP_NAME_IIMJOBS;

  const metaInfo = meta[APP_NAME];

  const metaData = isCommon
    ? Object.freeze(
        mergeDeep(
          {page, path},
          metaInfo[APP]['common'],
          metaInfo[APP]['subscription'],
          metaInfo[APP]['featureFlags'],
          metaInfo[APP][path] || {}
        )
      )
    : {
        ...metaInfo,
        app: metaInfo[APP][path],
        page,
        path,
        featureFlags: metaInfo[APP]['featureFlags'],
      };

  return metaData;
};

export const slugToTitle = (slug: string): string => {
  return slug.split('?')[0].replace('-jobs', '').replaceAll('-', ' ');
};

export const getMatchingCityName = (capitalisedLocation: string) => {
  const normalizedSpaces = capitalisedLocation
    .replaceAll('-', ' ')
    .toLowerCase();
  const normalizedSlashes = capitalisedLocation
    .replaceAll('-', '/')
    .toLowerCase();

  const matchedCity = locations.find(city => {
    const cityNameLower = city.label.toLowerCase();
    return (
      cityNameLower === normalizedSpaces || cityNameLower === normalizedSlashes
    );
  });

  return matchedCity ? matchedCity.label : null;
};
/* eslint-disable @typescript-eslint/no-explicit-any */
const removeEmpty = (obj: {[x: string]: any}) => {
  const newObj: {[x: string]: any} = {};
  Object.keys(obj).forEach(key => {
    if (obj[key] === Object(obj[key])) newObj[key] = removeEmpty(obj[key]);
    else if (obj[key] !== undefined) newObj[key] = obj[key];
  });
  return newObj;
};

export const removedUndefinedFromArrayObject = (arrayObj: Array<object>) => {
  if (Array.isArray(arrayObj)) {
    return arrayObj.map((obj: {[x: string]: any}) => {
      return removeEmpty(obj);
    });
  }
};

const getInheritCatOrTag = (
  tabList: Array<{dataId: string; label: string; tag: string; menu: Tabs[]}>,
  filteredSlug: string,
  cat?: boolean
) => {
  for (const tab of tabList) {
    const {menu: menuTab} = tab || {};

    const filteredTab = menuTab.filter(({tag, menu}) => {
      if (
        tag === filteredSlug &&
        (isAppIimjobs || (cat ? Array.isArray(menu) : true))
      )
        return true;

      if (Array.isArray(menu)) {
        return menu.some(
          ({tag, menu}) =>
            tag === filteredSlug &&
            (isAppIimjobs || (cat ? Array.isArray(menu) : true))
        );
      }
      return false;
    });

    if (filteredTab && filteredTab.length > 0) {
      if (Array.isArray(filteredTab[0]?.menu)) {
        const filteredTabMenu = filteredTab[0].menu.filter(
          ({tag, menu}) =>
            tag === filteredSlug &&
            (isAppIimjobs || (cat ? Array.isArray(menu) : true))
        );
        console.log('🚀 ~ filteredTabMenu:', filteredTabMenu);
        if (filteredTabMenu && filteredTabMenu.length > 0) {
          return {
            id: filteredTabMenu[0].dataId,
            title: filteredTabMenu[0].label,
            catId: parseInt(tab?.dataId),
            subCatId: parseInt(filteredTab[0]?.dataId ?? '0'),
          };
        }
      }

      return {
        id: filteredTab[0].dataId,
        title: filteredTab[0].label,
        catId: parseInt(tab?.dataId),
      };
    }
  }
};

const getIdBySlug = (
  page: string,
  slug: string,
  tabList: Array<{dataId: string; label: string; tag: string; menu: Tabs[]}>,
  swap = false,
  subCatagory = false
): {
  id: string;
  title: string;
  catId?: number;
  subCatId?: number;
  slug?: string;
} => {
  const filteredSlug = slugToTitle(slug);

  let filtered = {
    id: '',
    title: '',
    catId: -1,
  };

  const findTab = (key: 'dataId' | 'tag') =>
    tabList.find(tab => tab?.[key] === filteredSlug);
  let slugUrl = '';

  if (page === 'category' || page === 'categoryLocation') {
    const matchedTab = findTab(swap ? 'dataId' : 'tag');

    if (subCatagory) {
      const inherited = getInheritCatOrTag(
        tabList,
        filteredSlug,
        page === 'categoryLocation' && !isAppIimjobs ? true : false
      );
      if (inherited && Object.keys(inherited).length > 0) {
        filtered = inherited;
      }
    }

    if (matchedTab) {
      slugUrl = matchedTab.tag;
      filtered = {
        id: matchedTab.dataId,
        title: matchedTab.label.replace('Jobs', ''),
        catId: -1,
      };
    }
  } else if (page === 'tag') {
    const inherited = getInheritCatOrTag(tabList, filteredSlug);
    if (inherited && Object.keys(inherited).length > 0) {
      filtered = inherited;
    }
  }

  return {...filtered, slug: slugUrl};
};

const getLocationIds = (locationString: string) => {
  if (!locationString) {
    return '';
  }
  const locationLabels = locationString?.split('_');

  const locationIds: Array<number> = [];

  locationLabels.forEach(location => {
    locations.forEach(locObj => {
      if (
        location.replace('-', ' ').toLowerCase() === locObj?.label.toLowerCase()
      ) {
        locationIds.push(locObj?.value);
      }
    });
  });

  return locationIds?.join(',');
};

const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const getSelectedLocationIds = (locationString: string) => {
  if (!locationString) {
    return '';
  }

  let location;
  let found = false;
  locations.forEach(locObj => {
    const replaced = locationString
      .replaceAll('-', ' ')
      ?.replace(/[^A-Z0-9]+/gi, ' ')
      .toLowerCase();
    const local = locObj?.label?.replace(/[^A-Z0-9]+/gi, ' ')?.toLowerCase();

    if (replaced === local) {
      if (locObj?.selected.length > 0) {
        location = locObj.selected;
      } else {
        location = locObj?.value;
      }
      found = true;
    }
    // !isAppIimjobs check removed
    if (local.includes(replaced) && !found) {
      if (locObj?.selected.length > 0) {
        location = locObj.selected;
      } else {
        location = locObj?.value;
      }
    }
  });

  return location;
};

const getCategoryLabel = (tabsList: any, categoryId: number) => {
  const tags = [];
  for (let i = 0; i < tabsList.length; i++) {
    if (tabsList[i]?.dataId == categoryId) {
      return tabsList[i];
    }
    tags.push(...tabsList[i].menu);
  }

  for (let j = 0; j < tags.length; j++) {
    if (tags[j].dataId == categoryId) {
      return tags[j];
    }
  }
};

export const setQueryParams = (queryParams: any) => {
  const url = new URL(window?.location as unknown as string);
  const searchParams = new URLSearchParams(window.location.search);
  const newParams = new URLSearchParams();
  const updatedParams: {[key: string]: string | null} = {};

  for (const [key, value] of Array.from(searchParams.entries())) {
    updatedParams[key] = value;
  }

  for (const key in queryParams) {
    const isEmptyArray =
      Array.isArray(queryParams[key]) && queryParams[key].length === 0;
    if (
      queryParams[key] !== undefined &&
      queryParams[key] !== null &&
      queryParams[key] !== false &&
      queryParams[key] !== '' &&
      !isEmptyArray &&
      !(key === 'page' && queryParams[key] === 0)
    ) {
      updatedParams[key] = queryParams[key]?.toString();
    } else {
      updatedParams[key] = null;
    }
  }

  if (
    ((searchParams.get('minexp') === null ||
      searchParams.get('minexp') === '') &&
      (queryParams?.['minexp'] === undefined ||
        queryParams?.['minexp'] === null ||
        queryParams?.['minexp'] === '')) ||
    (queryParams?.['minexp'] === 0 && queryParams?.['maxexp'] === 0)
  ) {
    updatedParams['minexp'] = null;
    updatedParams['maxexp'] = null;
  }

  if (updatedParams['page'] && updatedParams['page'] !== null) {
    newParams.set('page', updatedParams['page']);
  }

  for (const [key, value] of Object.entries(updatedParams)) {
    if (key !== 'page' && value !== null) {
      newParams.set(key, value);
    }
  }
  url.search = newParams.toString();
  window.history.replaceState(null, '', url.toString());
};

export const isUserAgentForBot = (context: GetServerSidePropsContext) => {
  const userAgent = context.req?.headers['user-agent'];
  const botRegx = new RegExp(BOTUSERAGENTS.join('|'), 'i');
  return botRegx.test(userAgent ?? '');
};

export const cleanObj = (obj: {[key: string]: string | number}) => {
  for (const propName in obj) {
    if (!obj[propName] || obj[propName] === '0') {
      delete obj[propName];
    }
  }
  return obj;
};

export const capitalizeFirstLetter = (string: string) => {
  if (!string) {
    return '';
  }
  return string[0].toUpperCase() + string.slice(1);
};

export const truncateString = (str: string, index: number) => {
  if (!str || str.length <= index) {
    return str || '';
  }

  return str.slice(0, index) + '...';
};

export const getDeviceOs = (userAgent: string): string => {
  const isMobile =
    /(iPad|iPhone|Android|webOS|iPod|BlackBerry|IEMobile|Opera Mini|Mobile)/i.test(
      userAgent
    ) || false;

  if (isMobile) {
    if (
      /Chrome/i.test(userAgent) ||
      /Safari/i.test(userAgent) ||
      /Firefox/i.test(userAgent) ||
      /Mobile/i.test(userAgent)
    ) {
      return 'mobile';
    }

    if (/android|Android/i.test(userAgent)) {
      return 'android';
    }

    if (/iphone|iPhone|ipad|iPad|ipod|iPadOS|iOS|ios/.test(userAgent)) {
      return 'ios';
    }
  }

  return 'web';
};

export const utilServerSideDeviceDetection = (
  context: GetServerSidePropsContext
) => {
  const isServer = !!context.req;
  const userAgent: any = isServer
    ? context.req.headers['user-agent']
    : navigator.userAgent;
  const isLine = /\bLine\//i.test(userAgent) || false;
  const isMobile = /(iPad|iPhone|Android|Mobile)/i.test(userAgent) || false;
  const rules = [
    'WebView',
    '(iPhone|iPod|iPad)(?!.*Safari/)',
    'Android.*(wv|.0.0.0)',
  ];

  const regex = new RegExp(`(${rules.join('|')})`, 'ig');
  const isInApp = Boolean(userAgent?.match(regex));
  const deviceType = getDeviceOs(userAgent);

  return {
    deviceType,
    isMobile,
    isLine,
    isInApp,
    userAgent,
  };
};

const getMonth = (month: number) => {
  if (month > 12 || month < 1) {
    return '';
  }
  return [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ][month - 1];
};

const getDaysDifference = (startDateStr: string): number => {
  const startDate = dayjs(startDateStr, 'YYYY-MM-DD HH:mm:ss');
  const endDate = dayjs();
  const daysDifference = endDate.diff(startDate, 'days');

  return daysDifference;
};

const formatDateTime = (dateTime: string) => {
  const year = dateTime.slice(0, 4);
  const month = dateTime.slice(4, 6);
  const day = dateTime.slice(6, 8);
  const hour = dateTime.slice(9, 11);
  const minute = dateTime.slice(11, 13);

  return `${year}-${month}-${day}T${hour}:${minute}:00`;
};

interface SlotDetails {
  jobTitle: string;
  description: string;
  address: string;
  slotDate: string;
  slotTime: string;
}

export const generateCalendarLinks = ({
  jobTitle,
  description,
  address,
  slotDate,
  slotTime,
}: SlotDetails) => {
  // Parse time manually since customParseFormat may not work across module boundaries
  const timeMatch = slotTime.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  let hours = timeMatch ? parseInt(timeMatch[1], 10) : 0;
  const minutes = timeMatch ? parseInt(timeMatch[2], 10) : 0;
  const isPM = timeMatch ? timeMatch[3].toUpperCase() === 'PM' : false;

  if (isPM && hours !== 12) hours += 12;
  if (!isPM && hours === 12) hours = 0;

  const formattedDateTime = dayjs(slotDate)
    .hour(hours)
    .minute(minutes)
    .second(0);
  const formattedDate = formattedDateTime.format('h:mm A, dddd, Do MMM YYYY');

  if (dayjs(formattedDateTime).year() < 2000) {
    return {gCalLink: '', yCalLink: '', outlookUrl: '', formattedDate: 'n/a'};
  }

  const startTime = formattedDateTime.format('YYYYMMDDTHHmm');
  const endTime = formattedDateTime
    .clone()
    .add(30, 'minutes')
    .format('YYYYMMDDTHHmm');
  const newAddress = address.trim().split(' ').join('+');

  const gCalLinkStart = formattedDateTime
    .clone()
    .subtract(5, 'minutes')
    .format('YYYYMMDDTHHmm');
  const gCalLinkEnd = formattedDateTime
    .clone()
    .subtract(5, 'minutes')
    .add(30, 'minutes')
    .format('YYYYMMDDTHHmm');
  const gCalLink = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${jobTitle}&dates=${gCalLinkStart}/${gCalLinkEnd}&details=${description}&location=${newAddress}&pli=1&sf=true&output=xml#eventpage_6`;

  const yCalLink = `https://calendar.yahoo.com/?v=60&view=d&type=20&title=${jobTitle}&st=${startTime}&et=${endTime}&desc=${description}&in_loc=${newAddress}&uid=`;

  const outlookStart = formattedDateTime.format('YYYY-MM-DDTHH:mm:ss');
  const outlookEnd = formattedDateTime
    .add(30, 'minutes')
    .format('YYYY-MM-DDTHH:mm:ss');
  const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&startdt=${outlookStart}&enddt=${outlookEnd}&subject=${jobTitle}&body=${description}&location=${newAddress}`;

  return {gCalLink, yCalLink, outlookUrl, formattedDate};
};

const getKeyFromPath = (key: string) => {
  if (window && window.location) {
    const urlParams = new URLSearchParams(window.location.search);

    return urlParams.get(key);
  }

  return '';
};

function formatPrettyDate(dateString: string) {
  const date = new Date(dateString);

  const day = date.getDate();
  const month = date.toLocaleString('default', {month: 'long'});
  const year = date.getFullYear();

  const getOrdinal = (n: number) => {
    if (n > 3 && n < 21) return 'th';
    switch (n % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  const ordinalDay = `${day}${getOrdinal(day)}`;
  return `${ordinalDay} ${month} ${year}`;
}

function capitalizeWords(str: string): string {
  return str
    ? str
        .split(' ')
        .map(
          (word: string) =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' ')
    : str;
}
const getProfileSchema = async () => {
  const schema: {[key: string]: string} = {};

  try {
    if (process.env.NEXT_PUBLIC_PROFILE_SCHEMA) {
      const response = await fetch(process.env.NEXT_PUBLIC_PROFILE_SCHEMA);
      const profileSchema: RegistrationTypes.SchemaType = await response.json();

      if (profileSchema) {
        Object.keys(profileSchema)?.map((sectionName: string) => {
          Object.keys(profileSchema[sectionName])?.map((fieldKey: string) => {
            schema[
              `${sectionName}.${profileSchema[sectionName][fieldKey]['field']}`
            ] = fieldKey;
          });
        });
      }
    }
  } catch (err) {
    console.log('Error occurred while fetching the profile-schema', err);
  }

  return schema;
};

const formQueryParams = (ref: string) => {
  const params = ['utm_source', 'utm_medium', 'utm_campaign'];
  const finalRef = ref || getKeyFromPath('ref');
  let query = '';

  if (finalRef && finalRef !== 'null' && finalRef !== 'undefined') {
    query += `ref=${finalRef}`;
  }

  params.forEach(key => {
    const val = getKeyFromPath(key);
    if (val) query += query ? `&${key}=${val}` : `${key}=${val}`;
  });
  return query;
};

/**
 * Extracts query string from resolved URL and builds destination URL with query params
 * @param resolvedUrl - The resolved URL from Next.js context
 * @param baseRoute - The base route to redirect to (e.g., ROUTE_JOBFEED)
 * @returns The destination URL with query params if present
 */
export const buildDestinationWithQuery = (
  resolvedUrl: string,
  baseRoute: string
): string => {
  const queryString = resolvedUrl.includes('?')
    ? resolvedUrl.split('?')[1]
    : '';
  return queryString ? `${baseRoute}?${queryString}` : baseRoute;
};
const STALE_THRESHOLD_DAYS = 240;

/**
 * Check if recruiter page should be noindexed based on lastJobPostedDate
 * @param lastJobPostedDate - Date when last job was posted
 * @returns true if recruiter is stale (>240 days), false otherwise
 */
const isRecruiterStale = (
  lastJobPostedDate: string | Date | null | undefined
): boolean => {
  if (!lastJobPostedDate) return true;

  const lastJobDate = dayjs(lastJobPostedDate);
  if (!lastJobDate.isValid()) return true;

  const daysSinceLastJob = dayjs().diff(lastJobDate, 'days');

  return daysSinceLastJob > STALE_THRESHOLD_DAYS;
};

export {
  formatDateTime,
  getCategoryLabel,
  getConfigByApp,
  getDaysDifference,
  getIdBySlug,
  getKeyFromPath,
  formQueryParams,
  getLocationIds,
  getMonth,
  getSelectedLocationIds,
  formatPrettyDate,
  capitalizeWords,
  getProfileSchema,
  isRecruiterStale,
};
